import { ExecutionStep } from '@common/Execution/types';
import { useUserFabrics } from '@common/Fabric/hooks';
import { useIDEParams, useIdeState } from '@common/ide/state';
import { useSelector } from '@common/performance/useSelector';
import { CommonActionTypes } from '@common/types';
import { Entity } from '@common/types/Entity';
import { EntityEvents } from '@common/user-analytics';
import { AspectKind } from '@prophecy/graphql-types/enums';
import { clone } from '@prophecy/utils/data';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';

import { useGlobalVariables } from '../../context/globalConfig/securedConfigHook';
import { useMixPanel } from '../../context/mixpanel/context';
import { fetchJobLogList, fetchJobRunList } from '../../data/apis/api';
import { errorHandler, ExecutionServiceError, useInfiniteGraphQlQuery } from '../../data/util';
import { OBSERVATION_FABRIC } from '../../utils/localstorage-keys';
import {
  ConnectorEvent,
  DataQualityObservationQuery,
  DataQualityObservationResponse,
  FetchConnectorEventsQuery,
  FetchConnectorEventsResponse,
  FetchConnectorEventsVariables,
  ObservationType
} from './query';
import {
  actionTypes,
  ConnectorObservationMetadata,
  DQObservationMetadata,
  ObservationMetadata,
  ObservationState,
  ResetObservationKeyWord,
  RunType
} from './redux/types';
import { ConnectorEventTypes, JobLogVariableType, ProjectEventTypes } from './types';

const PAGE_LIMIT = 25;

export function useObservationIDE() {
  const store = useStore();
  const fabrics = useUserFabrics();
  const currentFabric = localStorage.getItem(OBSERVATION_FABRIC) || '';
  const isFabricFound = Boolean(fabrics.find((fabric) => fabric.id === currentFabric));
  const fabricId = isFabricFound ? currentFabric : fabrics?.[0]?.id;
  const project = useIdeState((state) => state.project);
  useEffect(() => {
    store.dispatch({ type: CommonActionTypes.loaderOn, payload: { hasCountSame: true } });
    store.dispatch({
      type: actionTypes.setProject,
      payload: { fabricId, project, event: fabricId ? ProjectEventTypes.JobRuns : ProjectEventTypes.AttentionRequired }
    });
  }, [project, fabricId, store]);

  const { track } = useMixPanel();

  useEffect(() => {
    track(EntityEvents[Entity.Dataset].ide.expectationIdeOpen);
  }, [track]);

  const changeProjectEvent = (eventType: ProjectEventTypes) => {
    store.dispatch({ type: actionTypes.setProjectEventType, payload: { eventType } });
  };
  const changeConnectorEvent = (eventType: ConnectorEventTypes) => {
    store.dispatch({ type: actionTypes.setConnetorEventType, payload: { eventType } });
  };
  const changeFabric = (fabricId: string) => {
    localStorage.setItem(OBSERVATION_FABRIC, fabricId);
    store.dispatch({ type: actionTypes.changeFabric, payload: fabricId });
  };
  return { store, changeProjectEvent, changeConnectorEvent, changeFabric };
}

export const useProjectObservationsData = () => {
  const selectedFiles = useIdeState((state) => state.ide.monitoringSelectedFiles);
  const expectationSelectedFiles = selectedFiles[Entity.Dataset];
  const projectId = useIdeState((state) => state.ide.uid);
  const uid = expectationSelectedFiles?.entityId;
  const dispatch = useDispatch();
  const isDQExpectationEnabled = useGlobalVariables().isDataQualityEnabled;
  const { track } = useMixPanel();

  useEffect(() => {
    if (projectId) {
      track(EntityEvents[Entity.Dataset].ide.observationSummaryOpen, { projectId });
    } else {
      track(EntityEvents[Entity.Dataset].ide.observationAllSummaryOpen);
    }
  }, [projectId, track]);

  const dq = useSelector<ObservationState, DQObservationMetadata>((state) => state.dataQuality);
  const meta = useSelector<ObservationState, ObservationMetadata>((state) => state.observationMetadata);

  const observationDate = meta.startDateFrom;
  const isObservationDate = Boolean(observationDate && observationDate !== ResetObservationKeyWord);
  const startDateTo = isObservationDate ? meta.startDateTo.getTime() : undefined;
  const variables = useMemo(
    () => ({
      uid: uid || undefined,
      projectId: projectId ? Number(projectId) : undefined,
      startDateTo,
      maxCount: PAGE_LIMIT,
      startDateFrom: observationDate === ResetObservationKeyWord ? undefined : (observationDate as number)
    }),
    [uid, observationDate, projectId, startDateTo]
  );

  const execMonitoringData = useJobRunsData({
    fabricId: meta.fabricId,
    startDateTo,
    startDateFrom: isObservationDate && observationDate ? Math.ceil(observationDate as number) : undefined
  });

  const {
    fetchNextPage,
    hasNextPage,
    isInitialLoading: isLoading,
    isFetchingNextPage
  } = useInfiniteGraphQlQuery(
    [Entity.Observation, variables],
    DataQualityObservationQuery,
    {
      enabled: Boolean(isDQExpectationEnabled),
      getNextPageParam: (lastPage, allPages) => {
        const lastPageObservations = (lastPage as DataQualityObservationResponse).DataQualityObservations;
        const count = lastPageObservations.length;
        const isLimitMatched = isObservationDate && allPages.length <= 1 ? count < 1 : count < PAGE_LIMIT;
        return isLimitMatched ? undefined : lastPageObservations[count - 1]?.startDate;
      },
      onSuccess: (resp) => {
        let observationData: ObservationType[] = [];
        const pages = resp?.pages ?? [];
        pages.forEach((pageData) => {
          observationData = observationData.concat(pageData.DataQualityObservations as unknown as ObservationType);
        });
        dispatch({
          type: actionTypes.setProjectObservations,
          payload: {
            observations: observationData
          }
        });
      }
    },
    variables
  );

  const loadMore = usePersistentCallback(async () => {
    dispatch({ type: actionTypes.setIsLoadingMoreData, payload: true });
    if (execMonitoringData.hasNextRunPage) {
      await execMonitoringData.fetchNextRunPage();
    }
    if (hasNextPage) {
      const observations = dq.observations;
      const observationsCount = observations.length;
      const startDateTo = observationsCount > 0 ? observations[observationsCount - 1].startDate : undefined;
      await fetchNextPage({ pageParam: { ...variables, startDateFrom: undefined, startDateTo } });
    }
    dispatch({ type: actionTypes.setIsLoadingMoreData, payload: false });
  });

  const handleStatusChange = (status?: string) => {
    dispatch({ type: actionTypes.setStatus, payload: { status } });
  };

  const handleDateChange = (date?: number | string) => {
    if (date && date !== ResetObservationKeyWord) {
      if (projectId) {
        track(EntityEvents[Entity.Dataset].ide.observationDateChange, {
          projectId,
          date: date ? new Date(date).toISOString() : ''
        });
      } else {
        track(EntityEvents[Entity.Dataset].ide.observationAllTabDateChange, {
          projectId,
          date: date ? new Date(date).toISOString() : ''
        });
      }
    }

    dispatch({ type: actionTypes.setObservationDate, payload: date });
  };

  return {
    loadMore,
    handleDateChange,
    hasNextPage,
    execMonitoringData,
    isLoading: isLoading || isFetchingNextPage,
    handleStatusChange
  };
};

export function useConnectorEventsData() {
  const fabricId = useSelector<ObservationState, string>((state) => state.observationMetadata.fabricId);
  const { uid } = useIDEParams();
  const dispatch = useDispatch();
  const meta = useSelector<ObservationState, ObservationMetadata>((state) => state.observationMetadata);
  const connectorObservation = useSelector<ObservationState, ConnectorObservationMetadata>(
    (state) => state.connectorObservation
  );
  const observationDate = meta.startDateFrom;
  const isObservationDate = Boolean(observationDate && observationDate !== ResetObservationKeyWord);
  const startDateTo = isObservationDate ? meta.startDateTo.getTime() : undefined;
  const startDateFrom = isObservationDate && observationDate ? Math.ceil(observationDate as number) : undefined;
  const variables: FetchConnectorEventsVariables = {
    ownerUID: fabricId,
    maxCount: PAGE_LIMIT,
    uid,
    startDateTo,
    startDateFrom
  };

  const {
    fetchNextPage,
    hasNextPage,
    isInitialLoading: isLoading,
    isFetching
  } = useInfiniteGraphQlQuery(
    [Entity.Observation, Entity.Connector, variables],
    FetchConnectorEventsQuery,
    {
      getNextPageParam: (lastPage, allPages) => {
        const lastPageObservations = (lastPage as FetchConnectorEventsResponse).FetchConnectorEvents;
        const count = lastPageObservations.length;
        const isLimitMatched = isObservationDate && allPages.length <= 1 ? count < 1 : count < PAGE_LIMIT;
        return isLimitMatched ? undefined : lastPageObservations[count - 1]?.created_at;
      },
      onSuccess: (resp) => {
        let events: ConnectorEvent[] = [];
        const pages = resp?.pages ?? [];
        pages.forEach((pageData) => {
          events = events.concat(pageData.FetchConnectorEvents);
        });
        dispatch({
          type: actionTypes.setConnectorEvents,
          payload: {
            events
          }
        });
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: Boolean(fabricId)
    },
    variables
  );

  const loadMore = usePersistentCallback(async () => {
    dispatch({ type: actionTypes.setIsLoadingMoreData, payload: true });
    if (hasNextPage) {
      const events = connectorObservation.events;
      const eventsCount = events.length;
      const startDateTo = eventsCount > 0 ? events[eventsCount - 1].created_at : undefined;
      await fetchNextPage({ pageParam: { ...variables, startDateFrom: undefined, startDateTo } });
    }
    dispatch({ type: actionTypes.setIsLoadingMoreData, payload: false });
  });

  return { loadMore, isLoading: isLoading || isFetching, hasNextPage };
}

const useExecutionUrl = (fabricId: string) => {
  const fabrics = useUserFabrics();
  return fabrics?.find((fabric) => fabric.id === fabricId)?.executionUrl as string;
};

export const useJobRunLogs = ({
  databricksRunId,
  prophecyJobId,
  databricksJobId,
  airflowDagName,
  airflowDagRunId,
  fabricId,
  enabled
}: JobLogVariableType & { fabricId: string; enabled: boolean }) => {
  const executionUrl = useExecutionUrl(fabricId);
  const resp = useQuery<ExecutionStep[], ExecutionServiceError>(
    ['job-run-logs', databricksRunId || airflowDagRunId],
    async () => {
      const resp = await fetchJobLogList(executionUrl, fabricId, {
        databricksRunId,
        databricksJobId,
        airflowDagName,
        airflowDagRunId,
        prophecyJobId
      });
      return resp.data as unknown as ExecutionStep[];
    },
    {
      enabled
    }
  );
  return resp;
};

const getDateFormatForAirflow = (date: number) => {
  const currentDate = new Date(date).toJSON().replace('Z', '');
  return currentDate + '520+00:00';
};

const useJobRunsData = ({
  fabricId,
  startDateFrom,
  startDateTo
}: {
  fabricId: string;
  startDateFrom?: number;
  startDateTo?: number;
}) => {
  const fabrics = useUserFabrics();
  const isAirflowFabric =
    fabrics?.find((f) => f.id === fabricId)?.AirflowProviderInfoAspect?.[0]?.AspectName ===
    AspectKind.AirflowProviderInfo;
  const monitoringSelectedFiles = useIdeState((state) => state.ide.monitoringSelectedFiles);
  const pipelineId = monitoringSelectedFiles[Entity.Pipeline]?.entityId;
  const jobId = monitoringSelectedFiles[Entity.Job]?.entityId;
  const datasetId = monitoringSelectedFiles[Entity.Dataset]?.entityId;
  const executionUrl = useExecutionUrl(fabricId);
  const dispatch = useDispatch();
  useEffect(() => {
    if (datasetId) {
      dispatch({ type: actionTypes.setProjectEventType, payload: { eventType: ProjectEventTypes.QualityIncidents } });
    }
  }, [datasetId, dispatch]);

  useEffect(() => {
    if (jobId || pipelineId) {
      dispatch({ type: actionTypes.setProjectEventType, payload: { eventType: ProjectEventTypes.JobRuns } });
    }
  }, [jobId, pipelineId, dispatch]);
  let projectId = useIdeState((state) => state.project.id);
  const startFrom = isAirflowFabric && startDateFrom ? getDateFormatForAirflow(startDateFrom) : startDateFrom;
  const startTo = isAirflowFabric && startDateTo ? getDateFormatForAirflow(startDateTo) : startDateTo;

  const variables = useMemo(
    () => ({ executionUrl, projectId, jobId, limit: PAGE_LIMIT, startDateTo: startTo, startDateFrom: startFrom }),
    [executionUrl, projectId, jobId, startFrom, startTo]
  );

  const loadData = usePersistentCallback(async (lastEventDate?: number) => {
    const isFirstLoad = startFrom && lastEventDate === startFrom;
    const resp = await fetchJobRunList(executionUrl, fabricId, {
      startDateFrom: isFirstLoad ? startFrom : undefined,
      startDateTo: isFirstLoad ? startTo : lastEventDate,
      prophecyJobId: jobId ? [jobId] : null,
      projectId: projectId || null,
      limit: PAGE_LIMIT
    });
    return resp.data;
  });

  const { data, hasNextPage, fetchNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery<
    RunType,
    ExecutionServiceError
  >(
    [Entity.Observation, fabricId, 'run-databricks-list', variables],
    ({ pageParam = startFrom }) => loadData(pageParam),
    {
      onError: errorHandler,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      onSuccess: () => dispatch({ type: actionTypes.setIsLoadingMoreData, payload: false }),
      enabled: Boolean(fabricId && executionUrl),
      getNextPageParam: (lastPage, allPages) => {
        let count = lastPage?.length;
        const isLimitMatched = startFrom && allPages.length <= 1 ? count < 1 : count < PAGE_LIMIT;
        return isLimitMatched ? undefined : lastPage[count - 1]?.startDate;
      }
    }
  );

  const getRunData = useCallback(() => {
    let runData: ObservationType[] = [];
    const pages = data?.pages ?? [];
    pages.forEach((pageData) => {
      let observations = clone(pageData) as unknown as ObservationType[];
      if (pipelineId) {
        observations = observations.filter((observation) =>
          observation?.pipelines?.find((pipeline) => pipeline.id === pipelineId)
        );
      }
      runData = runData.concat(observations);
    });
    return runData;
  }, [data, pipelineId]);

  return {
    runData: getRunData(),
    isLoadingRunData: isLoading || isFetchingNextPage,
    hasNextRunPage: hasNextPage,
    fetchNextRunPage: fetchNextPage
  };
};
